import { Check } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { CARD_EYEBROW } from "../../primitives/type";
import type { AttentionJourney as AttentionJourneyData } from "./types";

/**
 * The route a first-run card is walking the member along. A numbered device is
 * only honest when the content really is a sequence; this one is, so the rail
 * says where they are, what they have seen, and what is left.
 *
 * The icons come from the nav registry, so the mark on this rail is the same
 * mark they will hunt for in the sidebar afterwards.
 */
export function AttentionJourney({
  journey,
  opacityMix,
}: {
  journey: AttentionJourneyData;
  opacityMix: (pct: number) => string;
}) {
  const accent = "var(--attn-accent)";
  return (
    // mt-auto pins the rail to the bottom when the card has room. The rule
    // lives on an inner element with its own top margin so that when the card
    // is short and auto collapses to zero, the divider still clears the action
    // row instead of landing on it.
    <section className="relative z-10 mt-auto" aria-label={journey.label}>
      {/* Owns its own bottom space: mt-auto sits the rail on the content box
          edge, and the card contributes no padding below it, so without this
          the labels sit flush against the card border. */}
      <div className="mt-9 pb-5 pt-7" style={{ borderTop: "1px solid var(--attn-divider)" }}>
        <p className={cn(CARD_EYEBROW, "m-0 mb-4")} style={{ color: opacityMix(62) }}>
          {journey.label}
        </p>
        {/* The labels hang out of flow so each sits under its own node, so
            their height has to be reserved here or they spill past the card,
            which is exactly what happens once a short viewport squeezes the
            card and mt-auto collapses. */}
        <ol className="m-0 flex list-none items-start gap-0 p-0 pb-[26px]">
          {journey.steps.map((step, index) => {
            const isLast = index === journey.steps.length - 1;
            const done = step.state === "done";
            const current = step.state === "current";
            return (
              <li
                key={step.id}
                className={cn("flex min-w-0 flex-col", !isLast && "flex-1")}
                aria-current={current ? "step" : undefined}
              >
                <div className="flex w-full items-center">
                  <span className="relative shrink-0">
                    <span
                      className={cn(
                        "grid h-8 w-8 shrink-0 place-items-center rounded-full border transition-colors duration-300",
                        current &&
                          "shadow-[0_0_0_4px_color-mix(in_srgb,currentColor_12%,transparent)]",
                      )}
                      style={{
                        color: done || current ? accent : opacityMix(45),
                        borderColor:
                          done || current
                            ? `color-mix(in srgb, ${accent} 42%, transparent)`
                            : "var(--attn-divider)",
                        background: current
                          ? `color-mix(in srgb, ${accent} 12%, transparent)`
                          : "transparent",
                      }}
                    >
                      {done ? (
                        <Check size={14} weight="bold" aria-hidden="true" />
                      ) : step.Icon ? (
                        <step.Icon
                          size={15}
                          weight={current ? "duotone" : "regular"}
                          aria-hidden="true"
                        />
                      ) : null}
                    </span>
                    <span
                      className={cn(
                        CARD_EYEBROW,
                        "absolute top-full mt-2.5 whitespace-nowrap",
                        // First and last hug the card edges; everything between
                        // is centred on its own node.
                        index === 0 ? "left-0" : isLast ? "right-0" : "left-1/2 -translate-x-1/2",
                      )}
                      style={{ color: current ? opacityMix(92) : opacityMix(55) }}
                    >
                      {step.label}
                    </span>
                  </span>
                  {!isLast ? (
                    <span
                      aria-hidden="true"
                      className="mx-2 h-px flex-1"
                      style={{
                        background: done
                          ? `color-mix(in srgb, ${accent} 38%, transparent)`
                          : "var(--attn-divider)",
                      }}
                    />
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
